import qs from 'query-string';
import styled from '@emotion/styled';
import useReactRouter from 'use-react-router';
import { Switch, Route } from 'react-router-dom';
import { useLocation, matchPath } from 'react-router';
import React, { useMemo, useContext, useState } from 'react';
import useMeasureContentMain from '~/react-hooks/use-measure-content-main';
import { Panel } from '@vimily/bonjoro-ui-components';

import MessageSetup from '../Editor/MessageSetup';
import MessageContent from '../Editor/MessageContent';
import LandingPageDesign from '../Editor/LandingPageDesign';
import LandingPagePreview from '../Editor/LandingPagePreview';
import EmailTemplatePreview from '../Editor/EmailTemplatePreview';
import EditorContext, { EditorContextProps } from '../Editor/Context';
import Toggle from '../../Components/ToggleList';
import { PreviewValue, TemplateState } from '../Editor';
import { getHighlightForState } from '../HighlightWarning';

import { MessageTemplateType } from '~/types/models';
import { useFeatures } from '~/react-hooks/use-features';

const COLLAPSE_AT = 1024;

const Container = styled.div`
  position: relative;
  width: 100%;
  overflow-y: auto;
  padding: 15px 20px;
  display: flex;
  flex-wrap: wrap;
`;

const EditorContainer = styled.div`
  flex: 1 0 auto;
  width: 100%;
  @media (min-width: ${COLLAPSE_AT}px) {
    max-width: 370px;
    padding-right: 10px;
    flex: 0 0 30%;
  }
`;

const PreviewContainer = styled.div`
  width: 100%;
  flex: 1 0 auto;
  @media (min-width: ${COLLAPSE_AT}px) {
    flex: 1;
  }
`;

enum PanelState {
  MessageSetup = 'message-setup',
  MessageContent = 'message-content',
  LandingPageDesign = 'landing-page-design',
}

export default function Body() {
  const { canAccessNextGenTemplate } = useFeatures();
  const context: EditorContextProps = useContext(EditorContext);
  const { preview, get } = context;

  const isNextGen =
    get('email_template.template_type') === MessageTemplateType.NextGen;
  const showPreviewBtn = isNextGen && canAccessNextGenTemplate;

  const [activePanel, setActivePanel] = useState(PanelState.MessageSetup);
  const location = useLocation();
  const { height: contentMainHeight } = useMeasureContentMain();

  const { match } = useReactRouter<{ id: string }>();

  const deviceToggleListOptions = useMemo(
    () => [
      {
        icon: 'assets/images/icons/message-template/Desktop.svg',
        label: 'Desktop',
        url: `${location.pathname}?preview=${PreviewValue.Desktop}`,
        isActive: preview === PreviewValue.Desktop,
      },
      {
        icon: 'assets/images/icons/message-template/Mobile.svg',
        label: 'Mobile',
        url: `${location.pathname}?preview=${PreviewValue.Mobile}`,
        isActive: preview === PreviewValue.Mobile,
      },
    ],
    [location.pathname, preview]
  );

  const displayToggleListOptions = useMemo(
    () => [
      {
        icon: 'assets/images/icons/message-template/Landingpage.svg',
        label: 'Landing',
        url: `/message-templates/${match.params.id}/landing-page?${qs.stringify(
          { preview }
        )}`,
        isActive:
          matchPath(location.pathname, {
            path: [
              '/message-templates/:id/landing-page',
              '/message-templates/:id',
            ],
            exact: true,
          }) != null,
      },
      {
        icon: 'assets/images/icons/message-template/Inbox.svg',
        label: 'Inbox',
        url: `/message-templates/${
          match.params.id
        }/email-template?${qs.stringify({ preview })}`,
        isActive:
          matchPath(location.pathname, {
            path: '/message-templates/:id/email-template',
          }) != null,
      },
    ],
    [location.pathname, preview]
  );

  const panels = useMemo(
    () => [
      {
        name: PanelState.MessageSetup,
        title: 'Message Setup',
        overflowVisible: true,
        children: <MessageSetup />,
      },
      {
        name: PanelState.MessageContent,
        title: 'Message Content',
        children: <MessageContent />,
      },
      {
        name: PanelState.LandingPageDesign,
        title: 'Landing Page Design',
        children: <LandingPageDesign />,
      },
    ],
    []
  );

  return (
    <Container style={{ height: contentMainHeight - 65 }}>
      {getHighlightForState(context.get<TemplateState>('state'), isNextGen)}
      <EditorContainer>
        {panels.map(({ children, name, title, ...props }, index) => (
          <Panel
            key={index}
            active={activePanel === name}
            title={title}
            onClick={() => setActivePanel(name)}
            panelNumber={index + 1}
            {...props}
          >
            {children}
          </Panel>
        ))}
      </EditorContainer>
      <PreviewContainer>
        <Toggle
          options={displayToggleListOptions}
          showPreviewBtn={showPreviewBtn}
        />
        <div>
          <Switch>
            <Route
              path={`${match.path}/landing-page`}
              component={LandingPagePreview}
            />
            <Route
              path={`${match.path}/email-template`}
              component={EmailTemplatePreview}
            />
            <Route component={LandingPagePreview} />
          </Switch>
        </div>
        <Toggle options={deviceToggleListOptions} />
      </PreviewContainer>
    </Container>
  );
}
